import React, { useEffect, useMemo, useState } from 'react';
import PageShell from '@/components/Layout/PageShell';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useListAgentsQuery,
  useSetAgentStatusMutation,
  useDeleteAgentMutation,
  useCloneAgentMutation,
  Agent,
} from '@/redux/internal-services/agentsApi';

// ── small sub-components ──────────────────────────────────────────────────────

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, string> = {
    active: 'bg-green-50 text-green-600 border-green-100',
    disabled: 'bg-ui-subtle text-ui-text2 border-ui-border',
    deleted: 'bg-red-50 text-red-500 border-red-100',
    archived: 'bg-orange-50 text-orange-500 border-orange-100',
  };
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full border ${map[status] ?? map.disabled}`}>
      {status}
    </span>
  );
};

const AgentTypeBadge: React.FC<{ type?: string }> = ({ type }) => {
  if (!type) return null;
  const map: Record<string, string> = {
    conversational: 'bg-purple-50 text-purple-600 border-purple-100',
    workflow: 'bg-orange-50 text-orange-600 border-orange-100',
  };
  const label: Record<string, string> = {
    conversational: 'Chat',
    workflow: 'Workflow',
  };
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full border font-medium ${map[type] ?? 'bg-ui-subtle text-ui-text2 border-ui-border'}`}>
      {label[type] ?? type}
    </span>
  );
};

/** Initials avatar fallback or icon_url avatar */
const AgentAvatar: React.FC<{ agent: Agent }> = ({ agent }) => {
  const initials = (agent.agent_name || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

  // Deterministic hue from the agent name for visual variety
  const hue = Array.from(agent.agent_name || '').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360;

  if (agent.icon_url) {
    return (
      <img
        src={agent.icon_url}
        alt={agent.agent_name}
        className="h-8 w-8 rounded-full object-cover shrink-0"
      />
    );
  }

  return (
    <span
      className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0 select-none"
      style={{ background: `hsl(${hue},60%,48%)` }}
    >
      {initials}
    </span>
  );
};

// ── sort helpers ──────────────────────────────────────────────────────────────

type SortKey = 'name-asc' | 'name-desc' | 'updated';

function sortAgents(agents: Agent[], key: SortKey): Agent[] {
  return [...agents].sort((a, b) => {
    if (key === 'name-asc') return a.agent_name.localeCompare(b.agent_name);
    if (key === 'name-desc') return b.agent_name.localeCompare(a.agent_name);
    if (key === 'updated') {
      const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
      const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
      return tb - ta;
    }
    return 0;
  });
}

// ── page ──────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

const AgentsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name-asc');
  const [showArchived, setShowArchived] = useState(false);
  const [limit, setLimit] = useState(PAGE_SIZE);

  // Reset pagination whenever filters change
  useEffect(() => { setLimit(PAGE_SIZE); }, [search, statusFilter]);

  const { data, isLoading, refetch } = useListAgentsQuery({
    search: search || undefined,
    status: statusFilter || undefined,
    limit,
  });

  const hasMore = (data?.total ?? 0) > (data?.data?.length ?? 0);
  const [setStatus] = useSetAgentStatusMutation();
  const [deleteAgent] = useDeleteAgentMutation();
  const [cloneAgent] = useCloneAgentMutation();
  const [deleteConfirm, setDeleteConfirm] = useState<Agent | null>(null);
  const [cloneTarget, setCloneTarget] = useState<Agent | null>(null);
  const [cloneName, setCloneName] = useState('');

  const rawAgents = data?.data ?? [];

  // Filter out deleted/archived unless the user opts in
  const visibleAgents = useMemo(() => {
    const base = showArchived
      ? rawAgents
      : rawAgents.filter((a) => a.status !== 'deleted' && a.status !== 'archived');
    return sortAgents(base, sortKey);
  }, [rawAgents, showArchived, sortKey]);

  const hasArchivedAgents = rawAgents.some(
    (a) => a.status === 'deleted' || a.status === 'archived',
  );

  const toggle = async (a: Agent) => {
    await setStatus({
      id: a.agent_id,
      status: a.status === 'active' ? 'disabled' : 'active',
    })
      .unwrap()
      .catch(() => {});
    refetch();
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    await deleteAgent(deleteConfirm.agent_id).unwrap().catch(() => {});
    setDeleteConfirm(null);
    refetch();
  };

  const openCloneDialog = (a: Agent) => {
    setCloneName(`${a.agent_name} (copy)`);
    setCloneTarget(a);
  };

  const handleClone = async () => {
    if (!cloneTarget) return;
    const name = cloneName.trim() || `${cloneTarget.agent_name} (copy)`;
    await cloneAgent({ id: cloneTarget.agent_id, agent_name: name }).unwrap().catch(() => {});
    setCloneTarget(null);
    setCloneName('');
    refetch();
  };

  return (
    <PageShell
      maxWidth="max-w-5xl"
      header={
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-ui-text">AI Agents</h1>
              <p className="text-sm text-ui-text2 mt-1">Create and configure SAM agents available to tenants.</p>
            </div>
            <button
              onClick={() => navigate('/platform/agents/new')}
              className="self-start sm:self-auto flex items-center gap-2 bg-brand-dark text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-dark/90 transition"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Agent
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search agents..."
              className="flex-1 min-w-[180px] border border-ui-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40 bg-ui-input"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-ui-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40 bg-ui-input"
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              className="border border-ui-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40 bg-ui-input"
            >
              <option value="name-asc">Name A–Z</option>
              <option value="name-desc">Name Z–A</option>
              <option value="updated">Last Updated</option>
            </select>
            {(hasArchivedAgents || showArchived) && (
              <button
                type="button"
                onClick={() => setShowArchived((v) => !v)}
                className="flex items-center gap-2 text-sm text-ui-text2"
              >
                <span className={`relative h-5 w-9 rounded-full transition-colors inline-block shrink-0 ${showArchived ? 'bg-brand-dark' : 'bg-gray-300'}`}>
                  <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${showArchived ? 'left-[18px]' : 'left-0.5'}`} />
                </span>
                Show archived
              </button>
            )}
          </div>
        </div>
      }
    >

      {/* body */}
      {isLoading ? (
        <div className="py-20 text-center text-ui-text3 text-sm">Loading agents…</div>
      ) : visibleAgents.length === 0 && rawAgents.length === 0 ? (
        /* ── true empty state ── */
        <div className="border border-dashed border-ui-border rounded-xl py-20 text-center flex flex-col items-center gap-4">
          <div className="h-14 w-14 rounded-full bg-brand-light flex items-center justify-center">
            <svg className="h-7 w-7 text-brand-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15M14.25 3.104c.251.023.501.05.75.082M19.8 15l-1.8 1.8M5 14.5l-1.8 1.8M19.8 15a2.25 2.25 0 01.659 1.591v.659M5 14.5A2.25 2.25 0 004.341 16.09v.659m15.459 0a2.25 2.25 0 00-2.25 2.25H6.45a2.25 2.25 0 00-2.25-2.25m15.459 0H6.45" />
            </svg>
          </div>
          <div>
            <p className="text-ui-text font-medium mb-1">{t('platform.noAgentsYet')}</p>
            <p className="text-ui-text3 text-sm max-w-xs mx-auto">
              {t('platform.noAgentsYetDescription')}
            </p>
          </div>
          <button
            onClick={() => navigate('/platform/agents/new')}
            className="bg-brand-dark text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-dark/90 transition"
          >
            {t('platform.createFirstAgent')}
          </button>
        </div>
      ) : visibleAgents.length === 0 ? (
        /* ── filtered to zero ── */
        <div className="border border-dashed border-ui-border rounded-xl py-12 text-center">
          <p className="text-ui-text3 text-sm">{t('platform.noAgentsMatchFilters')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleAgents.map((a) => (

            <div
              key={a.agent_id}
              className={`bg-ui-card border border-ui-border2 rounded-xl p-5 shadow-sm flex items-start justify-between gap-4 border-l-4 ${a.status === 'active' ? 'border-l-emerald-400' : a.status === 'archived' || a.status === 'deleted' ? 'border-l-red-300' : 'border-l-gray-300'}`}
            >
              <div className="flex items-start gap-3 min-w-0">
                <AgentAvatar agent={a} />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold text-ui-text truncate">{a.agent_name}</h3>
                    <StatusPill status={a.status} />
                    <AgentTypeBadge type={a.agent_type} />
                    <span className="px-2 py-0.5 text-xs bg-ui-raised text-ui-text2 rounded-full border border-ui-border2">
                      {a.models?.text}
                    </span>
                    {a.tenant_id == null && (
                      <span className="px-2 py-0.5 text-xs bg-brand-light text-brand-dark rounded-full">
                        Global
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-ui-text2 mt-1 line-clamp-2">{a.description || '—'}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <button
                  onClick={() => toggle(a)}
                  className="text-xs px-2.5 py-1 rounded border text-ui-text2 border-ui-border hover:bg-ui-hover dark:hover:bg-white/10 transition"
                >
                  {a.status === 'active' ? 'Disable' : 'Enable'}
                </button>
                <button
                  onClick={() => navigate(`/platform/agents/${a.agent_id}`)}
                  className="text-xs text-brand-dark border border-brand-light px-2.5 py-1 rounded hover:bg-brand-light/50 transition"
                >
                  Edit
                </button>
                <button
                  onClick={() => openCloneDialog(a)}
                  className="text-xs text-ui-text2 border border-ui-border px-2.5 py-1 rounded hover:bg-ui-hover dark:hover:bg-white/10 transition"
                >
                  Clone
                </button>
                <button
                  onClick={() => setDeleteConfirm(a)}
                  className="text-xs text-red-500 border border-red-100 px-2.5 py-1 rounded hover:bg-red-50 transition"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}

          {hasMore && (
            <div className="pt-2 flex justify-center">
              <button
                onClick={() => setLimit((l) => l + PAGE_SIZE)}
                className="text-sm text-brand-dark border border-brand-light px-5 py-2 rounded-lg hover:bg-brand-light/50 transition"
              >
                Load more ({(data?.total ?? 0) - (data?.data?.length ?? 0)} remaining)
              </button>
            </div>
          )}
        </div>
      )}

      {/* clone name dialog */}
      {cloneTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-ui-card rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="text-lg font-bold text-ui-text mb-1">Clone Agent</h3>
            <p className="text-sm text-ui-text2 mb-4">Choose a name for the cloned agent.</p>
            <input
              autoFocus
              value={cloneName}
              onChange={(e) => setCloneName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleClone()}
              className="w-full border border-ui-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40 mb-5"
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setCloneTarget(null); setCloneName(''); }}
                className="flex-1 border border-ui-border text-ui-text py-2 rounded-lg text-sm hover:bg-ui-hover dark:hover:bg-white/10 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleClone}
                className="flex-1 bg-brand-dark text-white py-2 rounded-lg text-sm font-medium hover:bg-brand-dark/90 transition"
              >
                Clone
              </button>
            </div>
          </div>
        </div>
      )}

      {/* delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-ui-card rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="text-lg font-bold text-ui-text mb-2">Delete Agent</h3>
            <p className="text-sm text-ui-text2 mb-6">
              Delete <strong>{deleteConfirm.agent_name}</strong>? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 border border-ui-border text-ui-text py-2 rounded-lg text-sm hover:bg-ui-hover dark:hover:bg-white/10 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 bg-danger text-white py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default AgentsPage;
